import { isDefined } from "@clipboard-health/util-ts";
import { isBefore, parseISO } from "date-fns";

import {
  type ProvisionalExtraWorkedTimeRequest,
  type ProvisionalExtraWorkedTimeRequestResponse,
} from "../api/useGetProvisionalExtraWorkedTimeRequest";
import { getScheduledEndDate } from "./getScheduledEndDate";

function hasShiftExtensionRequest(
  provisionalRequests: ProvisionalExtraWorkedTimeRequest[]
): boolean {
  return provisionalRequests.some((request) => !isDefined(request.attributes.end));
}

interface GetIsShiftEndedWithoutExtensionParams {
  isExtendShiftFeatureEnabled: boolean;
  shiftStart: string;
  shiftEnd: string;
  clockInStart?: string;
  clockOutEnd?: string;
  isCapAtShiftLength: boolean;
  now: Date;
  provisionalExtraWorkedTimeRequest?: ProvisionalExtraWorkedTimeRequestResponse;
}

/**
 * Returns true when the shift's scheduled end time has passed, the worker has
 * not clocked out, and no shift extension request exists. Used on the focus
 * page to freeze the timer and show the shift-ended bottom sheet.
 *
 * For cap-at-shift-length, the scheduled end is computed as clockIn + shift duration
 * rather than the original shift.end.
 */
export function getIsShiftEndedWithoutExtension(
  params: GetIsShiftEndedWithoutExtensionParams
): boolean {
  const {
    isExtendShiftFeatureEnabled,
    shiftStart,
    shiftEnd,
    clockInStart,
    clockOutEnd,
    isCapAtShiftLength,
    now,
    provisionalExtraWorkedTimeRequest,
  } = params;

  if (!isExtendShiftFeatureEnabled) {
    return false;
  }

  const scheduledEndDate = getScheduledEndDate({
    shiftStartDate: parseISO(shiftStart),
    shiftEndDate: parseISO(shiftEnd),
    clockInDate: isDefined(clockInStart) ? parseISO(clockInStart) : undefined,
    isCapAtShiftLength,
  });

  const isNowPastShiftEnd = !isBefore(now, scheduledEndDate);
  const isNotClockedOut = !isDefined(clockOutEnd);
  const provisionalRequests = provisionalExtraWorkedTimeRequest?.data ?? [];
  const hasExtension = hasShiftExtensionRequest(provisionalRequests);

  return isNowPastShiftEnd && isNotClockedOut && !hasExtension;
}

interface GetWasClockedOutAfterShiftEndParams {
  isExtendShiftFeatureEnabled: boolean;
  shiftStart: string;
  shiftEnd: string;
  clockInStart?: string;
  clockOutEnd?: string;
  isCapAtShiftLength: boolean;
  provisionalExtraWorkedTimeRequest?: ProvisionalExtraWorkedTimeRequestResponse;
}

/**
 * Returns true when the worker clocked out at or after the scheduled shift end,
 * shift extension functionality is enabled, and no shift extension request exists. Used on
 * the timesheet page to lock the clock-out time to shift.end and show the MPR
 * warning.
 *
 * For cap-at-shift-length, the scheduled end is computed as clockIn + shift duration
 * rather than the original shift.end.
 */
export function getWasClockedOutAfterShiftEnd(
  params: GetWasClockedOutAfterShiftEndParams
): boolean {
  const {
    isExtendShiftFeatureEnabled,
    shiftStart,
    shiftEnd,
    clockInStart,
    clockOutEnd,
    isCapAtShiftLength,
    provisionalExtraWorkedTimeRequest,
  } = params;

  if (!isExtendShiftFeatureEnabled || !isDefined(clockOutEnd)) {
    return false;
  }

  const provisionalRequests = provisionalExtraWorkedTimeRequest?.data ?? [];
  if (hasShiftExtensionRequest(provisionalRequests)) {
    return false;
  }

  const scheduledEndDate = getScheduledEndDate({
    shiftStartDate: parseISO(shiftStart),
    shiftEndDate: parseISO(shiftEnd),
    clockInDate: isDefined(clockInStart) ? parseISO(clockInStart) : undefined,
    isCapAtShiftLength,
  });

  return !isBefore(parseISO(clockOutEnd), scheduledEndDate);
}

interface GetShouldShowClockOutAdjustmentNoticeParams {
  isExtendShiftFeatureEnabled: boolean;
  isShiftExtendable: boolean;
  hasShiftExtension: boolean;
  scheduledEndDate?: Date;
  clockOutDate?: Date;
}

/**
 * Returns true when the worker's clock-out was capped at the scheduled shift
 * end because they did not complete the shift-extension flow. Used to gate
 * the yellow notice pill rendered below the clock-out row on the timesheet.
 *
 * Same predicate as `getWasClockedOutAfterShiftEnd`, but:
 * - takes `Date` inputs so callers can pass the live form value while editing
 *   (so the pill disappears as the picker moves earlier than scheduled end);
 * - additionally requires `isShiftExtendable=true` so observability-only
 *   states like `SHIFT_EXTENSION_DISABLED` (feature on, UI hidden) don't
 *   render the notice when the worker never had access to the extension flow.
 */
export function getShouldShowClockOutAdjustmentNotice(
  params: GetShouldShowClockOutAdjustmentNoticeParams
): boolean {
  const {
    isExtendShiftFeatureEnabled,
    isShiftExtendable,
    hasShiftExtension,
    scheduledEndDate,
    clockOutDate,
  } = params;

  return (
    isExtendShiftFeatureEnabled &&
    isShiftExtendable &&
    !hasShiftExtension &&
    isDefined(scheduledEndDate) &&
    isDefined(clockOutDate) &&
    !isBefore(clockOutDate, scheduledEndDate)
  );
}
